import { Box } from '@chakra-ui/react';
import Scrollbars, { ScrollValues } from 'rc-scrollbars';
import { ForwardedRef, forwardRef, MutableRefObject, ReactNode, useCallback, useState } from 'react';

type Props = {
  children: ReactNode;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  viewWidth?: string;
  viewHeight?: string;
  onScroll?: () => void;
  scrollbarRef?: MutableRefObject<Scrollbars | null>;
  defaultOverflow?: boolean;
};

const CockpitCustomScrollbar = forwardRef(
  (
    {
      children,
      onScroll,
      scrollbarRef,
      marginTop = 0.5,
      marginRight = 0,
      marginBottom = 0,
      marginLeft = 1,
      viewWidth,
      viewHeight,
      defaultOverflow = false,
    }: Readonly<Props>,
    ref: ForwardedRef<unknown>,
  ) => {
    const [scrollbarVisible, setScrollbarVisible] = useState(defaultOverflow);

    const refSetter = useCallback(
      (scrollbars: (Scrollbars & { view: HTMLDivElement }) | null) => {
        const scrollbar = scrollbars ? scrollbars : null;
        if (ref) {
          if (typeof ref === 'function') {
            ref(scrollbar?.view);
          } else {
            ref.current = scrollbar?.view;
          }
        }
        if (scrollbarRef) {
          scrollbarRef.current = scrollbar;
        }
      },
      [ref, scrollbarRef],
    );

    const handleUpdate = ({ scrollHeight, clientHeight }: ScrollValues) => {
      setScrollbarVisible(scrollHeight > clientHeight);
    };
    return (
      <Scrollbars
        onScroll={onScroll}
        onUpdate={handleUpdate}
        disableDefaultStyles
        ref={refSetter}
        renderTrackVertical={(props) => (
          <Box
            {...props}
            style={{
              position: 'absolute',
              width: '8px',
              height: `calc(100% - ${(marginTop + marginBottom) * 8}px)`,
              right: 0,
              top: 0,
            }}
            marginLeft={marginLeft}
            marginTop={marginTop}
            marginRight={marginRight}
            backgroundColor="transparent"
          />
        )}
        renderThumbVertical={({ style, ...props }) => (
          <Box style={{ ...style }} width="8px" backgroundColor="neutral.600" {...props} />
        )}
        style={{ flexGrow: 0, overflow: 'hidden', backgroundColor: 'transparent' }}
      >
        <Box
          width={scrollbarVisible ? (viewWidth ?? `calc(100% - ${(marginRight + marginLeft) * 8 + 8}px)`) : '100%'}
          height={viewHeight ?? `calc(100% - ${(marginTop + marginBottom) * 8}px)`}
          position="relative"
        >
          {children}
        </Box>
      </Scrollbars>
    );
  },
);

export default CockpitCustomScrollbar;
